/**
 * A function to create a log object for the 'dummyEvent' event (evid: 900)
 *
 * Event description: 901-949 appSet events; 950-999 widget events; 1000+ BE events
 *
 * @param {dummyEventParams} params - The parameters for this event
 * @property {string} artifactVersion
 * @property {GUID} playerInstanceID
 * @property {GUID} sid
 * @property {string} sessionId
 * @return {LogObject<dummyEventParams>} LogObject<dummyEventParams>
 */
export function dummyEvent(params) {
    return { evid: 900, src: 9, endpoint: 'podcast', params: params };
}
/**
 * A function to create a log object for the 'mainEpisodeClick' event (evid: 901)
 *
 * Event description: User clicks on Episode in episode list on main podcast page
 *
 * @param {mainEpisodeClickParams} params - The parameters for this event
 * @property {GUID} playerInstanceID - mandatory
 * @property {GUID} sid - mandatory
 * @property {GUID} _msid - mandatory
 * @property {GUID} visitorId - visitor_id, mandatory
 * @property {string} feedId - feed id
 * @property {string} artifactVersion - app version for debug, not mandatory
 * @property {GUID} _appId - app_id
 * @property {GUID} _instanceId - mandatory
 * @property {string} _viewMode - view_mode
 * @property {GUID} _siteMemberId - site_member_id
 * @property {string} sessionId - session id
 * @property {string} compId - player_id
 * @property {string} episodeId - episode id
 * @property {GUID} _visitorId - visitor_id, mandatory
 * @return {LogObject<mainEpisodeClickParams>} LogObject<mainEpisodeClickParams>
 */
export function mainEpisodeClick(params) {
    return { evid: 901, src: 9, endpoint: 'podcast', params: params };
}
/**
 * A function to create a log object for the 'feedLoadRequested' event (evid: 902)
 *
 * Event description: Widget feed load requested
 *
 * @param {feedLoadRequestedParams} params - The parameters for this event
 * @property {GUID} playerInstanceID - mandatory
 * @property {GUID} sid - mandatory
 * @property {GUID} _msid - mandatory
 * @property {GUID} visitorId - visitor_id, mandatory
 * @property {string} feedId - feed id
 * @property {boolean} isServerSide - is server
 * @property {string} artifactVersion - app version for debug, not mandatory
 * @property {GUID} _appId - app_id
 * @property {GUID} _instanceId - mandatory
 * @property {string} _viewMode - view_mode
 * @property {GUID} _siteMemberId - site_member_id
 * @property {string} sessionId - session id
 * @property {string} compId - player_id
 * @property {string} pageType - main / episode
 * @property {string} correlationId - one uniq guid per one load process
 * @property {GUID} _visitorId - visitor_id, mandatory
 * @return {LogObject<feedLoadRequestedParams>} LogObject<feedLoadRequestedParams>
 */
export function feedLoadRequested(params) {
    return { evid: 902, src: 9, endpoint: 'podcast', params: params };
}
/**
 * A function to create a log object for the 'feedLoadCompleted' event (evid: 903)
 *
 * Event description: Widget feed load completed
 *
 * @param {feedLoadCompletedParams} params - The parameters for this event
 * @property {GUID} playerInstanceID - mandatory
 * @property {GUID} sid - mandatory
 * @property {GUID} _msid - mandatory
 * @property {boolean} success - true / false
 * @property {GUID} visitorId - visitor_id, mandatory
 * @property {string} feedId - feed id
 * @property {boolean} isServerSide - is server
 * @property {string} artifactVersion - app version for debug, not mandatory
 * @property {GUID} _appId - app_id
 * @property {GUID} _instanceId - mandatory
 * @property {string} errorType - error
 * @property {string} _viewMode - view_mode
 * @property {GUID} _siteMemberId - site_member_id
 * @property {string} sessionId - session id
 * @property {string} compId - player_id
 * @property {number} loadTime - in ms
 * @property {string} pageType - main / episode
 * @property {string} correlationId - one uniq guid per one load process
 * @property {GUID} _visitorId - visitor_id, mandatory
 * @return {LogObject<feedLoadCompletedParams>} LogObject<feedLoadCompletedParams>
 */
export function feedLoadCompleted(params) {
    return { evid: 903, src: 9, endpoint: 'podcast', params: params };
}
/**
 * A function to create a log object for the 'episodeEnclosureError' event (evid: 904)
 *
 * Event description: Widget play error
 *
 * @param {episodeEnclosureErrorParams} params - The parameters for this event
 * @property {GUID} playerInstanceID - mandatory
 * @property {GUID} sid - mandatory
 * @property {GUID} _msid - mandatory
 * @property {GUID} visitorId - visitor_id, mandatory
 * @property {string} feedId - feed id
 * @property {string} enclosureUrl - enclosure url
 * @property {string} artifactVersion - app version for debug, not mandatory
 * @property {GUID} _appId - app_id
 * @property {GUID} _instanceId - mandatory
 * @property {string} errorType - error
 * @property {string} _viewMode - view_mode
 * @property {GUID} _siteMemberId - site_member_id
 * @property {string} sessionId - session id
 * @property {string} compId - player_id
 * @property {string} correlationId - correlation with play start
 * @property {GUID} _visitorId - visitor_id, mandatory
 * @return {LogObject<episodeEnclosureErrorParams>} LogObject<episodeEnclosureErrorParams>
 */
export function episodeEnclosureError(params) {
    return { evid: 904, src: 9, endpoint: 'podcast', params: params };
}
/**
 * A function to create a log object for the 'navigationRequested' event (evid: 905)
 *
 * Event description: User of user tries to go to another page in a app
 *
 * @param {navigationRequestedParams} params - The parameters for this event
 * @property {GUID} playerInstanceID - mandatory
 * @property {GUID} sid - mandatory
 * @property {GUID} _msid - mandatory
 * @property {GUID} visitorId - visitor_id, mandatory
 * @property {string} feedId - feed id
 * @property {string} artifactVersion - app version for debug, not mandatory
 * @property {GUID} _appId - app_id
 * @property {GUID} _instanceId - mandatory
 * @property {string} _viewMode - view_mode
 * @property {GUID} _siteMemberId - site_member_id
 * @property {string} sessionId - session id
 * @property {string} compId - player_id
 * @property {string} episodeId - episode id
 * @property {string} pageType - (where users tries to go (episode / main)
 * @property {string} correlationId - one uniq guid per one load process
 * @property {GUID} _visitorId - visitor_id, mandatory
 * @return {LogObject<navigationRequestedParams>} LogObject<navigationRequestedParams>
 */
export function navigationRequested(params) {
    return { evid: 905, src: 9, endpoint: 'podcast', params: params };
}
/**
 * A function to create a log object for the 'navigationCompleted' event (evid: 906)
 *
 * Event description: User of user goes to another page in an app
 *
 * @param {navigationCompletedParams} params - The parameters for this event
 * @property {GUID} playerInstanceID - mandatory
 * @property {GUID} sid - mandatory
 * @property {GUID} _msid - mandatory
 * @property {boolean} success - true / false
 * @property {GUID} visitorId - visitor_id, mandatory
 * @property {string} feedId - feed id
 * @property {string} artifactVersion - app version for debug, not mandatory
 * @property {GUID} _appId - app_id
 * @property {GUID} _instanceId - mandatory
 * @property {string} errorType - error
 * @property {string} _viewMode - view_mode
 * @property {GUID} _siteMemberId - site_member_id
 * @property {string} sessionId - session id
 * @property {string} compId - player_id
 * @property {string} episodeId - episode id
 * @property {string} pageType - (where users tries to go (episode / main)
 * @property {string} correlationId - one uniq guid per one load process
 * @property {GUID} _visitorId - visitor_id, mandatory
 * @return {LogObject<navigationCompletedParams>} LogObject<navigationCompletedParams>
 */
export function navigationCompleted(params) {
    return { evid: 906, src: 9, endpoint: 'podcast', params: params };
}
/**
 * A function to create a log object for the 'episodePlayRequested' event (evid: 907)
 *
 * Event description: User clicks on the play on podcast player
 *
 * @param {episodePlayRequestedParams} params - The parameters for this event
 * @property {GUID} playerInstanceID - mandatory
 * @property {GUID} sid - mandatory
 * @property {GUID} _msid - mandatory
 * @property {number} duration - don't send, we got it in 911
 * @property {GUID} visitorId - visitor_id, mandatory
 * @property {string} feedId - feed id
 * @property {string} artifactVersion - app version for debug, not mandatory
 * @property {GUID} _appId - app_id
 * @property {GUID} _instanceId - mandatory
 * @property {string} _viewMode - view_mode
 * @property {GUID} _siteMemberId - site_member_id
 * @property {string} sessionId - session id
 * @property {string} compId - player_id
 * @property {string} episodeId - episode id
 * @property {string} correlationId - one uniq guid per one load process
 * @property {string} type - video / track
 * @property {GUID} _visitorId - visitor_id, mandatory
 * @return {LogObject<episodePlayRequestedParams>} LogObject<episodePlayRequestedParams>
 */
export function episodePlayRequested(params) {
    return { evid: 907, src: 9, endpoint: 'podcast', params: params };
}
/**
 * A function to create a log object for the 'episodePlayStarted' event (evid: 908)
 *
 * Event description: User clicks on the play on podcast player and play starts
 *
 * @param {episodePlayStartedParams} params - The parameters for this event
 * @property {GUID} playerInstanceID - mandatory
 * @property {GUID} sid - mandatory
 * @property {GUID} _msid - mandatory
 * @property {number} duration - don't send, we got it in 911
 * @property {GUID} visitorId - visitor_id, mandatory
 * @property {string} feedId - feed id
 * @property {string} artifactVersion - app version for debug, not mandatory
 * @property {GUID} _appId - app_id
 * @property {GUID} _instanceId - mandatory
 * @property {string} _viewMode - view_mode
 * @property {GUID} _siteMemberId - site_member_id
 * @property {string} sessionId - session id
 * @property {string} compId - player_id
 * @property {string} episodeId - episode id
 * @property {string} correlationId - one uniq guid per one load process
 * @property {string} type - video / track
 * @property {GUID} _visitorId - visitor_id, mandatory
 * @return {LogObject<episodePlayStartedParams>} LogObject<episodePlayStartedParams>
 */
export function episodePlayStarted(params) {
    return { evid: 908, src: 9, endpoint: 'podcast', params: params };
}
/**
 * A function to create a log object for the 'episodeDownloadClick' event (evid: 909)
 *
 * Event description: User clicks on episode download button
 *
 * @param {episodeDownloadClickParams} params - The parameters for this event
 * @property {GUID} playerInstanceID - mandatory
 * @property {GUID} sid - mandatory
 * @property {GUID} _msid - mandatory
 * @property {GUID} visitorId - visitor_id, mandatory
 * @property {string} feedId - feed id
 * @property {string} url - download url
 * @property {string} artifactVersion - app version for debug, not mandatory
 * @property {GUID} _appId - app_id
 * @property {GUID} _instanceId - mandatory
 * @property {string} _viewMode - view_mode
 * @property {GUID} _siteMemberId - site_member_id
 * @property {string} sessionId - session id
 * @property {string} compId - player_id
 * @property {string} episodeId - episode id
 * @property {string} type - video / track
 * @property {GUID} _visitorId - visitor_id, mandatory
 * @return {LogObject<episodeDownloadClickParams>} LogObject<episodeDownloadClickParams>
 */
export function episodeDownloadClick(params) {
    return { evid: 909, src: 9, endpoint: 'podcast', params: params };
}
/**
 * A function to create a log object for the 'episodeNavigationClick' event (evid: 910)
 *
 * Event description: User clicks on Previous Episode / Next episode / all episodes navigation
 *
 * @param {episodeNavigationClickParams} params - The parameters for this event
 * @property {GUID} playerInstanceID - mandatory
 * @property {GUID} sid - mandatory
 * @property {GUID} _msid - mandatory
 * @property {GUID} visitorId - visitor_id, mandatory
 * @property {string} feedId - feed id
 * @property {string} artifactVersion - app version for debug, not mandatory
 * @property {GUID} _appId - app_id
 * @property {string} requestedEpisodeId - requested episode id
 * @property {string} button - next / previous
 * @property {GUID} _instanceId - mandatory
 * @property {string} _viewMode - view_mode
 * @property {GUID} _siteMemberId - site_member_id
 * @property {string} sessionId - session id
 * @property {string} compId - player_id
 * @property {string} episodeId - episode id current
 * @property {GUID} _visitorId - visitor_id, mandatory
 * @return {LogObject<episodeNavigationClickParams>} LogObject<episodeNavigationClickParams>
 */
export function episodeNavigationClick(params) {
    return { evid: 910, src: 9, endpoint: 'podcast', params: params };
}
/**
 * A function to create a log object for the 'widgetEpisodeStats' event (evid: 911)
 *
 * Event description: Episode current state, send after episode page open
 *
 * @param {widgetEpisodeStatsParams} params - The parameters for this event
 * @property {GUID} playerInstanceID - mandatory
 * @property {GUID} sid - mandatory
 * @property {GUID} _msid - mandatory
 * @property {GUID} visitorId - visitor_id, mandatory
 * @property {string} feedId - feed id
 * @property {string} artifactVersion - app version for debug, not mandatory
 * @property {boolean} hasDescription - is plain text description existing
 * @property {GUID} _appId - app_id
 * @property {GUID} _instanceId - mandatory
 * @property {string} _viewMode - view_mode
 * @property {GUID} _siteMemberId - site_member_id
 * @property {string} sessionId - session id
 * @property {string} compId - player_id
 * @property {string} episodeId - episode id
 * @property {DateTime} dateUpdated - created / updated date
 * @property {string} type - video / track / download_only
 * @property {GUID} _visitorId - visitor_id, mandatory
 * @property {boolean} hasImage - is image existing
 * @return {LogObject<widgetEpisodeStatsParams>} LogObject<widgetEpisodeStatsParams>
 */
export function widgetEpisodeStats(params) {
    return { evid: 911, src: 9, endpoint: 'podcast', params: params };
}
/**
 * A function to create a log object for the 'loadMoreClick' event (evid: 912)
 *
 * Event description: UoU clicks on Load More on main podcast page
 *
 * @param {loadMoreClickParams} params - The parameters for this event
 * @property {GUID} playerInstanceID - instance_id
 * @property {GUID} sid - msid
 * @property {GUID} _msid - msid
 * @property {string} feedId - feed id
 * @property {string} artifactVersion - artifact_version
 * @property {GUID} _appId - app_id
 * @property {GUID} _instanceId - instance_id
 * @property {string} _viewMode - view_mode
 * @property {GUID} _siteMemberId - site_member_id
 * @property {string} sessionId - session_id
 * @property {string} compId - player_id
 * @property {GUID} _visitorId - visitor_id
 * @return {LogObject<loadMoreClickParams>} LogObject<loadMoreClickParams>
 */
export function loadMoreClick(params) {
    return { evid: 912, src: 9, endpoint: 'podcast', params: params };
}
/**
 * A function to create a log object for the 'loadMoreCompleted' event (evid: 913)
 *
 * Event description: UoU clicks on Load More on main podcast page and episodes loaded
 *
 * @param {loadMoreCompletedParams} params - The parameters for this event
 * @property {GUID} playerInstanceID - instance_id
 * @property {GUID} sid - msid
 * @property {GUID} _msid - msid
 * @property {string} feedId - feed id
 * @property {string} artifactVersion - artifact_version
 * @property {number} episodesCount - new loaded episodes shown
 * @property {GUID} _appId - app_id
 * @property {GUID} _instanceId - instance_id
 * @property {string} _viewMode - view_mode
 * @property {GUID} _siteMemberId - site_member_id
 * @property {string} sessionId - session_id
 * @property {string} compId - player_id
 * @property {number} loadTime - ms
 * @property {GUID} _visitorId - visitor_id
 * @return {LogObject<loadMoreCompletedParams>} LogObject<loadMoreCompletedParams>
 */
export function loadMoreCompleted(params) {
    return { evid: 913, src: 9, endpoint: 'podcast', params: params };
}
//# sourceMappingURL=index.js.map